import { Modal } from 'react-bootstrap';
import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { userProfileApi } from '../../../services/userProfileApi'
import { Button } from 'reactstrap'
import { configureToastr, triggerToast } from '../../../utils/toast'
import SkeletonCard from '../../../pages/KeywordTracking/components/SkeletonCard'
import "../../../pages/UserProfile/PlansComponent.css"
import "./AppsTrackingPlanModal.css"
import axios from 'axios'
import { api } from '../../../utils/variables'
import UpgradePlanModal from './UpgradePlanModal'
import { navigateToRegisterForUpgrade, storePostPaymentRedirect, clearPostPaymentRedirect } from '../../../utils/loginRedirect'
import { useAnalytics, ANALYTICS_EVENTS } from '../../../analytics'

// Abbreviation tooltips for benefits
const ABBREVIATION_TOOLTIPS = {
  KTR: 'Keyword Relevance',
  AUR: 'Average User Rating',
  URV: 'User Reviews Volume',
  // Common abbreviations seen in UI — add meanings here as needed
  ASR: 'App Store Rank',
  KU: 'Keyword Usage',
};

// Safely escape strings for use in RegExp
const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderBenefitValueWithTooltips = (value) => {
  const text = typeof value === 'string' ? value : String(value ?? '');

  // Build a dynamic, case-insensitive regex from ABBREVIATION_TOOLTIPS keys
  const keys = Object.keys(ABBREVIATION_TOOLTIPS);
  if (keys.length === 0) {
    return <span className="benefit-text">{text}</span>;
  }

  // Sort by length desc to prefer longer matches first, and escape special chars
  const pattern = keys
    .map(k => escapeRegExp(k))
    .sort((a, b) => b.length - a.length)
    .join('|');

  const parts = text.split(new RegExp(`(${pattern})`, 'gi'));

  return (
    <span className="benefit-text">
      {parts.map((part, idx) => {
        const key = typeof part === 'string' ? part.toUpperCase() : part;
        if (ABBREVIATION_TOOLTIPS[key]) {
          return (
            <abbr
              key={idx}
              title={ABBREVIATION_TOOLTIPS[key]}
              style={{ textDecoration: 'underline dotted', cursor: 'help' }}
            >
              {part}
            </abbr>
          );
        }
        return <React.Fragment key={idx}>{part}</React.Fragment>;
      })}
    </span>
  );
};

// Helper utilities for price display
const formatPriceDisplay = (price) => {
  const p = Number(price);
  return p === 0 ? 'Free' : `$${price}`;
};

const isFreePrice = (price) => Number(price) === 0;

const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_live_51RT5QxLDJ9fsfGZRrpY58fAeZcrHLTVnEFd9Gpn5bdw0qyLNSfN6UIFPKSnBMEk9MFK5Un2zn3gXh3VOoLzIA3kk00Tsk7I9KA'

const loadStripeManually = () => {
  return new Promise((resolve, reject) => {
    if (window.Stripe) {
      resolve(window.Stripe(STRIPE_PUBLIC_KEY));
      return;
    }

    const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        if (window.Stripe) {
          resolve(window.Stripe(STRIPE_PUBLIC_KEY));
        } else {
          reject(new Error('Stripe object not available after script load'));
        }
      });
      existingScript.addEventListener('error', () => {
        reject(new Error('Failed to load existing Stripe script'));
      });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      setTimeout(() => {
        if (window.Stripe) {
          resolve(window.Stripe(STRIPE_PUBLIC_KEY));
        } else {
          reject(new Error('Stripe script loaded but object not available'));
        }
      }, 100);
    };

    script.onerror = (error) => {
      console.error('Stripe script load error:', error);
      reject(new Error('Failed to load Stripe script'));
    };

    document.head.appendChild(script);
  });
};

const AppsTrackingPlanModal = ({
  show,
  handleClose,
  title,
  searchTerm,
  onSearchChange,
  onSearchClick,
  results = [],
  loading = false,
  onSelectApp,
  trialMode = false,
  // Optional: where Stripe checkout should bring the user back to (via
  // /payment-success or /payment-cancel) instead of the dashboard — e.g. the
  // shared report they were unlocking. See storePostPaymentRedirect.
  returnPath,
  // Optional: called after an in-place plan change that never leaves the
  // page (the paid-to-paid upgrade below), so the caller can refresh
  // whatever the new plan unlocks.
  onPlanChanged,
}) => {
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()

  // Guests can't buy/trial a plan - two shapes exist in this app (see
  // api_helper.js's `isGuest` check / appTrackingv2.jsx's same pattern):
  // (1) fully anonymous - no `authToken` at all, and
  // (2) "continue as guest" - a real `authToken` is present, but
  // `authUser.data.is_guest`/`authUser.is_guest` is true.
  // Send either to sign up (see redirectGuestToUpgradeSignup below) instead
  // of letting the request hit the backend and surface a generic error toast.
  const isGuestOrLoggedOut = () => {
    const hasAuthToken = Boolean(localStorage.getItem('authToken'))
    if (!hasAuthToken) return true
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser'))
      return authUser?.data?.is_guest === true || authUser?.is_guest === true
    } catch (e) {
      return false
    }
  }

  // The ONE place in the app a logged-out/guest visitor's plan click gets
  // routed onward — every Upgrade/Unlock CTA site-wide shares this modal, so
  // fixing it here is what keeps the "click a plan while logged out ->
  // sign up -> land on billing" flow consistent everywhere, with zero
  // per-page duplication. See navigateToRegisterForUpgrade for the sign-up +
  // post-signup billing redirect itself.
  const redirectGuestToUpgradeSignup = () => {
    const isGuest = Boolean(localStorage.getItem('authToken'))
    try { handleClose() } catch (e) { /* ignore */ }
    navigateToRegisterForUpgrade(navigate, isGuest)
  }

  const [stripeLoaded, setStripeLoaded] = useState(false)
  const [stripeInstance, setStripeInstance] = useState(null)
  const [loadingStates, setLoadingStates] = useState({})
  const [loadingError, setLoadingError] = useState(null)

  const [plans, setPlans] = useState([])
  const [plansLoading, setPlansLoading] = useState(true)
  const [plansError, setPlansError] = useState(null)
  const [currentPlanId, setCurrentPlanId] = useState(null)
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState(null)
  const [currentSubscriptionStatus, setCurrentSubscriptionStatus] = useState(null)
  const [currentSubscriptionEndDate, setCurrentSubscriptionEndDate] = useState(null)
  const [currentSubscriptionStartDate, setCurrentSubscriptionStartDate] = useState(null)
  const [allBenefitNames, setAllBenefitNames] = useState([])
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelPlanData, setCancelPlanData] = useState(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [trialLoadingKey] = useState('trial')
  const trialInProgressRef = useRef(false)
  const [trialActivated, setTrialActivated] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradePlanData, setUpgradePlanData] = useState(null)
  const [isPremiumUser, setIsPremiumUser] = useState(false)

  useEffect(() => {
    if (!show) return

    // Reset displayed data immediately to avoid flicker of stale content
    setPlans([])
    setAllBenefitNames([])
    setPlansError(null)
    setPlansLoading(true)

    let cancelled = false
      ; (async () => {
        try {
          const stripe = await loadStripe(STRIPE_PUBLIC_KEY)
          if (!cancelled && stripe) {
            setStripeInstance(stripe)
            setStripeLoaded(true)
            setLoadingError(null)
            return
          }
        } catch (e) {
          console.error('Stripe package load failed:', e)
        }
        try {
          const manual = await loadStripeManually()
          if (!cancelled && manual) {
            setStripeInstance(manual)
            setStripeLoaded(true)
            setLoadingError(null)
          } else if (!cancelled) {
            throw new Error('Manual Stripe load returned null')
          }
        } catch (err) {
          if (!cancelled) {
            console.error('Stripe initialization failed:', err)
            setStripeLoaded(false)
            setLoadingError(err.message)
          }
        }
      })()
    return () => { cancelled = true }
  }, [show])

  const fetchCurrentPlan = async () => {
    try {
      const result = await userProfileApi.fetchCurrentPlan();

      if (result.data) {
        // Mirror PlansComponent logic: determine plan and subscription id
        const planId = result.data.product;
        const subscriptionId = result.data.subscription_id || result.data.id || null;
        const subscriptionStatus = result.data.status || result.data.subscription_status || null;
        const subscriptionEnd = result.data.end_date || result.data.trial_end || result.data.expires_at || null;
        const subscriptionStart = result.data.start_date || result.data.trial_start || result.data.started_at || null;

        setCurrentSubscriptionStatus(subscriptionStatus);
        setCurrentSubscriptionEndDate(subscriptionEnd);
        setCurrentSubscriptionStartDate(subscriptionStart);

        const isSubscriptionActive = subscriptionId &&
          subscriptionId !== null &&
          subscriptionStatus !== 'cancelled' &&
          subscriptionStatus !== 'canceled';

        // Handle cancelled_but_active status - keep plan active so "Ends on" shows
        const isCancelledButActive = subscriptionStatus === 'cancelled_but_active';

        // Set current plan if subscription is active OR cancelled but still active
        if (isSubscriptionActive || isCancelledButActive) {
          setCurrentPlanId(planId);
          setCurrentSubscriptionId(subscriptionId);
        } else {
          setCurrentPlanId(null);
          setCurrentSubscriptionId(null);
        }

        // Return all relevant data for use in fetchPlans
        return {
          planId,
          subscriptionId,
          subscriptionStatus,
          subscriptionEnd,
          subscriptionStart
        };
      }
    } catch (error) {
      console.error('Error fetching current plan:', error);
      setCurrentPlanId(null);
      setCurrentSubscriptionId(null);
      return null;
    }
    return null;
  };

  const fetchPlans = async (currentPlanData) => {
    try {
      const result = await userProfileApi.fetchPlans();
      if (result.data && Array.isArray(result.data)) {
        // Use freshly fetched data when available, otherwise fall back to state
        const effectivePlanId = currentPlanData?.planId ?? currentPlanId;
        const effectiveSubscriptionId = currentPlanData?.subscriptionId ?? currentSubscriptionId;
        const effectiveSubscriptionStatus = currentPlanData?.subscriptionStatus ?? currentSubscriptionStatus;
        const effectiveSubscriptionEnd = currentPlanData?.subscriptionEnd ?? currentSubscriptionEndDate;

        // Check if user currently has a paid subscription
        const hasPaidPlan =
          (effectivePlanId !== null && effectivePlanId !== undefined) ||
          (currentPlanId !== null && currentSubscriptionId !== null);

        // ✅ Check if subscription is cancelled but still active
        const isCancelledButActive = effectiveSubscriptionStatus === 'cancelled_but_active';

        // ✅ Check if cancelled plan has ended (end_date has passed)
        const hasCancelledPlanEnded = isCancelledButActive && effectiveSubscriptionEnd
          ? new Date(effectiveSubscriptionEnd).getTime() <= Date.now()
          : false;

        // Detect Basic / Premium plans and whether the user is currently on Premium
        const basicPlanFromApi = result.data.find(
          (plan) => plan.name && plan.name.toLowerCase().includes('basic')
        );
        const premiumPlanFromApi = result.data.find(
          (plan) => plan.name && plan.name.toLowerCase().includes('premium')
        );

        const isUserOnPremium =
          !!premiumPlanFromApi &&
          premiumPlanFromApi.id === effectivePlanId &&
          hasPaidPlan;
        try { setIsPremiumUser(!!isUserOnPremium) } catch (e) { /* ignore */ }

        // Track the currently active paid plan for use in the upgrade modal
        let currentPlanForUpgrade = null;

        // Exclude Explorer/default plan from modal
        const upgradePlans = result.data.filter(p => !p.is_default);

        const benefitNamesSet = new Map();
        upgradePlans.forEach(plan => {
          if (plan.description && Array.isArray(plan.description)) {
            plan.description.forEach((benefit, idx) => {
              if (!benefitNamesSet.has(benefit.benefit_name)) {
                benefitNamesSet.set(benefit.benefit_name, idx);
              }
            });
          }
        });
        const allNames = Array.from(benefitNamesSet.keys());
        setAllBenefitNames(allNames);

        const activeCurrentPlanId = effectivePlanId;

        const transformedPlans = upgradePlans.map((plan, index) => {
          const isCurrentPlan = activeCurrentPlanId === plan.id;

          // Base disabled state: current plan cannot be clicked
          let isDisabled = isCurrentPlan;

          // If user is on Premium, disable the Basic plan to prevent downgrades
          const isBasicPlan =
            basicPlanFromApi && plan.id === basicPlanFromApi.id;
          if (isUserOnPremium && isBasicPlan) {
            isDisabled = true;
          }

          // If user has a cancelled but active plan that hasn't ended, disable all other plans
          if (isCancelledButActive && !hasCancelledPlanEnded && !isCurrentPlan) {
            isDisabled = true;
          }

          let benefits = [];
          if (plan.description && Array.isArray(plan.description)) {
            benefits = plan.description;
          } else if (plan.all_benefits && Array.isArray(plan.all_benefits)) {
            benefits = plan.all_benefits.map(benefit => ({
              state: 'positive',
              benefit_name: benefit,
              benefit_value: benefit
            }));
          }

          // Capture current paid plan for upgrade modal
          const isPaidPlan = Number(plan.price) > 0;
          if (isCurrentPlan && isPaidPlan) {
            currentPlanForUpgrade = {
              id: plan.id,
              title: plan.name,
              price: plan.price,
              benefits,
              subscriptionId: effectiveSubscriptionId,
            };
          }

          // Determine button text and action
          let buttonText;
          let buttonAction;

          if (isCurrentPlan) {
            buttonText = 'Current Plan';
            buttonAction = null;
          } else if (trialMode) {
            // Trial modal: keep existing "7 days free trial" behavior
            buttonText = '7 days free trial';
            buttonAction = () => handleTrialClick(plan.id, plan.name);
          } else {
            // Main upgrade modal: if user already has a paid plan and this plan is
            // a more expensive paid plan, open UpgradePlanModal instead of Stripe.
            if (
              hasPaidPlan &&
              isPaidPlan &&
              currentPlanForUpgrade &&
              Number(plan.price) > Number(currentPlanForUpgrade.price)
            ) {
              buttonText = 'Upgrade';
              buttonAction = () =>
                openUpgradeModal(currentPlanForUpgrade, {
                  id: plan.id,
                  title: plan.name,
                  price: plan.price,
                  benefits,
                });
            } else {
              // Default behavior: show Free label for free plans, 'Upgrade' for paid plans
              buttonText = isFreePrice(plan.price) ? 'Free Plan' : 'Upgrade';
              buttonAction = () => handleClick(plan.id, plan.name);
            }
          }

          return {
            id: plan.id,
            title: plan.name,
            price: plan.price,
            benefits: benefits,
            buttonText,
            buttonAction,
            isDisabled: isDisabled,
            isPopular: index === 0,
            isDefault: isCurrentPlan,
            // Free trial metadata (if provided by API)
            freeTrialDays: plan.free_trial_days,
            eligibleFreeTrial: plan.eligible_free_trial,
          };
        });
        setPlans(transformedPlans);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setPlansError(error.message);
    } finally {
      setPlansLoading(false);
    }
  };

  const activateTrial = async (planId) => {
    const token = localStorage.getItem('authToken')
    if (!token) return
    if (trialInProgressRef.current) return
    trialInProgressRef.current = true
    setLoadingStates(prev => ({ ...prev, [trialLoadingKey]: true }))
    try {
      await axios.post(`${api}/user/onboarding/update`, {
        is_onboarded: true,
        onboarding_state: { ai_screen: true }
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        }
      })
      // clear session flag and mark trial as activated
      try { sessionStorage.removeItem('show_trial_modal_after_ai') } catch (e) { }
      setTrialActivated(true)
    } catch (e) {
      console.error('Activate trial failed', e)
    } finally {
      setLoadingStates(prev => ({ ...prev, [trialLoadingKey]: false }))
      trialInProgressRef.current = false
    }
  }

  // No automatic trial activation: user may click a plan to start Stripe checkout.

  useEffect(() => {
    if (!show) return
    let active = true
      ; (async () => {
        const currentPlanData = await fetchCurrentPlan()
        await fetchPlans(currentPlanData)
      })()
    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const handleClick = async (productId, planName) => {
    if (isGuestOrLoggedOut()) {
      redirectGuestToUpgradeSignup()
      return
    }
    let currentStripe = stripeInstance;
    if (!currentStripe && window.Stripe) {
      currentStripe = window.Stripe(STRIPE_PUBLIC_KEY);
    }
    if (!currentStripe) {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!currentStripe && window.Stripe) {
        currentStripe = window.Stripe(STRIPE_PUBLIC_KEY);
      }
      if (!currentStripe) {
        const errorMsg = loadingError
          ? `Payment system error: ${loadingError}. Please refresh the page.`
          : 'Payment system is still loading. Please wait a moment and try again.';
        alert(errorMsg);
        return;
      }
    }

    setLoadingStates(prev => ({ ...prev, [productId]: true }));
    try {
      const data = await userProfileApi.createPaymentSession(productId);
      if (!data.session_id) {
        throw new Error('No session_id in response');
      }
      if (returnPath) storePostPaymentRedirect(returnPath)
      else clearPostPaymentRedirect()
      const { error } = await currentStripe.redirectToCheckout({ sessionId: data.session_id });
      if (error) {
        clearPostPaymentRedirect()
        throw new Error(`Payment redirect failed: ${error.message}`);
      }
    } catch (err) {
      console.error('Payment error:', err);
      try { configureToastr() } catch (e) { }
      const msg = String(err.message || err || '');
      if (msg.includes('You already have an active subscription')) {
        try { triggerToast('You already have an active subscription', 'warning') } catch (e) { }
      } else {
        try { triggerToast(`Payment error: ${msg}`, 'error') } catch (e) { }
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleTrialClick = async (productId, planName) => {
    if (isGuestOrLoggedOut()) {
      redirectGuestToUpgradeSignup()
      return
    }
    // First, activate trial for this plan if not already done for this plan
    if (!trialActivated) {
      await activateTrial(productId)
    }

    // After activation, still allow the user to proceed to Stripe if they
    // choose to buy this plan (keeps previous behavior intact).
    try {
      await handleClick(productId, planName)
    } catch (e) {
      // handleClick already logs and toasts; swallow here
    }
  }

  const findBenefitForPlan = (plan, benefitName) => {
    if (plan && plan.benefits && Array.isArray(plan.benefits)) {
      const found = plan.benefits.find(b => b.benefit_name === benefitName);
      return found ? found : { state: 'negative', benefit_value: 'Not available' };
    }
    return { state: 'negative', benefit_value: 'Not available' };
  };

  // Helper to compute remaining trial days for the *current* subscription
  const getCurrentTrialInfoForPlan = (plan) => {
    try {
      if (
        !plan ||
        plan.id !== currentPlanId ||
        currentSubscriptionStatus !== 'trialing' ||
        !currentSubscriptionEndDate
      ) {
        return null;
      }

      const endMs = new Date(currentSubscriptionEndDate).getTime();
      const nowMs = Date.now();
      if (!Number.isFinite(endMs) || endMs <= nowMs) {
        return null;
      }

      const DAY_MS = 24 * 60 * 60 * 1000;

      // Days left (ceil so partial day counts as a full remaining day)
      const daysLeft = Math.max(0, Math.ceil((endMs - nowMs) / DAY_MS));

      // Total trial length
      let totalDays = plan.freeTrialDays ? Number(plan.freeTrialDays) : null;

      // If backend provides a start_date, derive total days from it
      if (!totalDays && currentSubscriptionStartDate) {
        const startMs = new Date(currentSubscriptionStartDate).getTime();
        if (Number.isFinite(startMs) && startMs < endMs) {
          totalDays = Math.max(1, Math.ceil((endMs - startMs) / DAY_MS));
        }
      }

      // Fallback to 7 days if nothing else is available
      if (!totalDays || !Number.isFinite(totalDays)) {
        totalDays = 7;
      }

      return { daysLeft, totalDays };
    } catch (e) {
      return null;
    }
  };

  const isComingSoonFeature = (benefitName, benefit) => {
    // Defensive checks
    if (!benefitName || typeof benefitName !== 'string') return false;

    const name = benefitName.toLowerCase().trim();

    // Explicit exceptions (never show Coming Soon for these exact features)
    const exceptions = [
      'auto reports & alerts',
      'auto reports',
      'reports & alerts',
      // Exclude keyword/category rank rows from coming-soon
      'keyword & category rank',
      'keyword & category ranks',
      'keyword & category rank(s)',
      'keyword & category',
      'keyword & category rank(s)',
      // Exclude sentiment analysis from coming-soon
      'sentiment analysis',
      'sentiment',
      'ai sentiment analysis'
    ];

    if (exceptions.includes(name)) return false;

    // Honor explicit coming_soon flag from the API (unless the feature is in exceptions)
    if (benefit && (benefit.coming_soon === true || benefit.comingSoon === true)) return true;

    // Also treat explicit 'coming soon' markers in benefit values as coming soon
    if (
      benefit &&
      benefit.benefit_value &&
      typeof benefit.benefit_value === 'string' &&
      benefit.benefit_value.toLowerCase().includes('coming soon')
    ) {
      return true;
    }

    return false;
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async (subscriptionId, planName) => {
    setLoadingStates(prev => ({ ...prev, [`cancel_${subscriptionId}`]: true }));
    setIsCancelling(true);

    try {
      await userProfileApi.cancelSubscription(subscriptionId);

      // Reset current plan state so UI shows Buy/Upgrade buttons
      setCurrentPlanId(null);
      setCurrentSubscriptionId(null);

      // Refresh plan list (pass null to use state values)
      await fetchPlans(null);

      setShowCancelModal(false);
      setCancelPlanData(null);

    } catch (err) {
      console.error('Cancellation error:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, [`cancel_${subscriptionId}`]: false }));
      setIsCancelling(false);
    }
  };

  // Open upgrade modal (fetches Stripe proration preview before showing)
  const openUpgradeModal = async (currentPlan, targetPlan) => {
    if (!currentPlan?.subscriptionId || !targetPlan?.id) {
      setUpgradePlanData({ currentPlan, targetPlan, preview: null });
      setShowUpgradeModal(true);
      return;
    }

    try {
      const preview = await userProfileApi.getUpgradePreview(
        currentPlan.subscriptionId,
        targetPlan.id
      );

      setUpgradePlanData({ currentPlan, targetPlan, preview });
      setShowUpgradeModal(true);
    } catch (error) {
      console.error('Error fetching upgrade preview:', error);
      setUpgradePlanData({ currentPlan, targetPlan, preview: null });
      setShowUpgradeModal(true);
    }
  };

  // Handle confirmed plan upgrade (no downgrades)
  const handlePlanUpgrade = async (currentPlan, targetPlan) => {
    setLoadingStates(prev => ({ ...prev, [`upgrade_${targetPlan.id}`]: true }));
    try {
      await userProfileApi.upgradeSubscription(currentPlan.subscriptionId, targetPlan.id);

      setShowUpgradeModal(false);
      setUpgradePlanData(null);

      // Refresh current plan and plans in this modal
      const currentPlanData = await fetchCurrentPlan();
      await fetchPlans(currentPlanData);
      onPlanChanged?.();
    } catch (err) {
      console.error('Upgrade error:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, [`upgrade_${targetPlan.id}`]: false }));
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="xl" className="modal_key app_modal_main">
      {title && (
        <Modal.Header closeButton>
          <Modal.Title>
            {isPremiumUser && title === 'Upgrade your plan to unlock this feature'
              ? 'Exceeded the limit'
              : title}
          </Modal.Title>
        </Modal.Header>
      )}
      <Modal.Body>
        {plansLoading ? (
          <div className="plans-container">
            <div className="modal-skeleton-cards">
              {[...Array(2)].map((_, idx) => (
                <SkeletonCard key={idx} />
              ))}
            </div>
          </div>
        ) : plansError ? (
          <div className="plans-container">
            <div className="alert alert-danger" role="alert">
              {plansError}
            </div>
          </div>
        ) : (
          <div className="plans-container">
            {!stripeLoaded && !loadingError && (
              <div className="text-center mb-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading Stripe...</span>
                </div>
                <span className="ms-2">Loading payment system...</span>
              </div>
            )}

            {loadingError && (
              <div className="alert alert-warning" role="alert" style={{ marginBottom: 16 }}>
                <strong>Payment System Warning:</strong> {loadingError}
                <br />
                <small>You can still view plans, but payments may not work. Please refresh the page.</small>
              </div>
            )}

            <div className="modal-plans-grid-wrapper">
              <div
                className="plans-grid"
                style={{ gridTemplateColumns: `200px repeat(${plans.length}, 1fr)` }}
              >
                <div className="cell feature-header"><h5>Features</h5></div>
                {plans.map((plan) => (
                  <div key={plan.id} className="cell plan-header-cell">
                    <div className="plan-header-content">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <h5 className="plan-title" style={{ margin: 0 }}>{plan.title}</h5>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {plan.isPopular && <div className="popular-badge popular-badge--inline">Most Popular</div>}
                        </div>
                      </div>
                      <div className="plan-price-container">
                        <div className="plan-price-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                            <span className="plan-price">{formatPriceDisplay(plan.price)}</span>
                            {!isFreePrice(plan.price) && <span className="plan-period">/month</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {plan.id === currentPlanId && currentSubscriptionStatus && currentSubscriptionStatus.toLowerCase().includes('cancel') && currentSubscriptionEndDate && (
                              <span className="cancel-tag" style={{ fontSize: '7px' }}>
                                Ends on: {new Date(currentSubscriptionEndDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            )}
                            {(() => {
                              const trialInfo = getCurrentTrialInfoForPlan(plan);
                              if (trialInfo) {
                                // User is actively on trial - show remaining days
                                return (
                                  <span className="trial-badge">
                                    Trial left {trialInfo.daysLeft} day{trialInfo.daysLeft !== 1 ? 's' : ''}
                                  </span>
                                );
                              }
                              // User hasn't taken trial yet - show original "X Days Free Trial" format
                              return (
                                !isFreePrice(plan.price) &&
                                plan.eligibleFreeTrial &&
                                Number(plan.freeTrialDays) > 0 && (
                                  <span className="trial-badge">
                                    {Number(plan.freeTrialDays)} Day{Number(plan.freeTrialDays) > 1 ? 's' : ''} Free Trial
                                  </span>
                                )
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                      <Button
                        className="plan-button"
                        onClick={() => {
                          trackEvent(ANALYTICS_EVENTS.CONVERSION.PLAN_UPGRADE_STARTED, { plan_id: plan.id, plan_name: plan.name })
                          plan.buttonAction?.()
                        }}
                        disabled={
                          plan.isDisabled ||
                          loadingStates[plan.id] ||
                          loadingStates['trial'] ||
                          (!stripeLoaded && !trialMode)
                        }
                      >
                        {(loadingStates['trial'] || loadingStates[plan.id]) ? 'Processing...' : plan.buttonText}
                      </Button>
                      {/* <Button
  className="plan-button"
  disabled
>
  {plan.buttonText}
</Button> */}
                    </div>
                  </div>
                ))}

                {allBenefitNames.map((benefitName, rowIdx) => (
                  <React.Fragment key={rowIdx}>
                    <div className="cell feature-name" style={{ position: 'relative', display: 'inline-block', minWidth: '205px' }}>
                      {benefitName}
                      {isComingSoonFeature(benefitName, findBenefitForPlan(plans[0], benefitName)) && (
                        <div style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                          color: 'white',
                          fontSize: '9px',
                          fontWeight: 600,
                          padding: '1px 5px 1px 2px',
                          borderRadius: '3px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 1px 3px rgba(255, 107, 53, 0.25)',
                          whiteSpace: 'nowrap'
                        }}>
                          Coming Soon
                        </div>
                      )}
                    </div>
                    {plans.map((plan) => {
                      const benefit = findBenefitForPlan(plan, benefitName);
                      return (
                        <div key={`${plan.id}-${benefitName}`} className="cell plan-benefit-cell">
                          <span className={`benefit-icon ${benefit.state}`}>
                            {benefit.state === 'positive' ? (
                              <svg width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M15.9422 1.06719L5.94219 11.0672C5.82496 11.1845 5.66588 11.2505 5.5 11.2505C5.33412 11.2505 5.17504 11.1845 5.05781 11.0672L0.682812 6.69219C0.438599 6.44797 0.438599 6.05203 0.682812 5.80781C0.927026 5.5636 1.32297 5.5636 1.56719 5.80781L5.5 9.74141L15.0578 0.182813C15.302 -0.0614009 15.698 -0.0614009 15.9422 0.182813C16.1864 0.427026 16.1864 0.822974 15.9422 1.06719Z" fill="#4CAF50" />
                              </svg>
                            ) : (
                              <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M13.2071 3.79289C13.5976 4.18342 13.5976 4.81658 13.2071 5.20711L10.4142 8L13.2071 10.7929C13.5976 11.1834 13.5976 11.8166 13.2071 12.2071C12.8166 12.5976 12.1834 12.5976 11.7929 12.2071L9 9.41421L6.20711 12.2071C5.81658 12.5976 5.18342 12.5976 4.79289 12.2071C4.40237 11.8166 4.40237 11.1834 4.79289 10.7929L7.58579 8L4.79289 5.20711C4.40237 4.81658 4.40237 4.18342 4.79289 3.79289C5.18342 3.40237 5.81658 3.40237 6.20711 3.79289L9 6.58579L11.7929 3.79289C12.1834 3.40237 12.8166 3.40237 13.2071 3.79289Z" fill="#F44336" />
                              </svg>
                            )}
                          </span>
                          {renderBenefitValueWithTooltips(benefit.benefit_value)}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Cancel subscription button (bottom center) */}
            {currentSubscriptionId && currentSubscriptionStatus !== 'cancelled_but_active' && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <Button
                  color="danger"
                  className="cancel-action-button"
                  onClick={() => {
                    const planName = (plans.find(p => p.id === currentPlanId) || {}).title || 'your subscription';
                    setCancelPlanData({ subscriptionId: currentSubscriptionId, planName });
                    setShowCancelModal(true);
                  }}
                  disabled={isCancelling || loadingStates[`cancel_${currentSubscriptionId}`]}
                >
                  {loadingStates[`cancel_${currentSubscriptionId}`] ? 'Cancelling...' : 'Cancel Subscription'}
                </Button>
              </div>
            )}

            {/* Cancellation Confirmation Modal */}
            {showCancelModal && cancelPlanData && (
              <div className="modal-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}>
                <div className="modal-content" style={{
                  backgroundColor: '#171821',
                  border: '1px solid rgba(53, 55, 72, 1)',
                  borderRadius: '12px',
                  padding: '24px',
                  maxWidth: '400px',
                  width: '90%',
                  color: '#ffffff'
                }}>
                  <h4 style={{ marginBottom: '16px', color: '#ffffff' }}>
                    Cancel Subscription
                  </h4>
                  <p style={{ marginBottom: '24px', color: 'rgba(161, 172, 184, 1)' }}>
                    Are you sure you want to cancel your <strong>{cancelPlanData.planName}</strong> subscription?
                  </p>
                  <p style={{ marginBottom: '24px', color: 'rgba(161, 172, 184, 1)', fontSize: '14px' }}>
                    You will continue to have access until the end of your current billing period.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <Button
                      color="secondary"
                      onClick={() => {
                        setShowCancelModal(false);
                        setCancelPlanData(null);
                      }}
                      style={{ minWidth: '80px' }}
                    >
                      Keep Plan
                    </Button>
                    <Button
                      color="danger"
                      className="cancel-action-button"
                      onClick={() => handleCancelSubscription(cancelPlanData.subscriptionId, cancelPlanData.planName)}
                      disabled={loadingStates[`cancel_${cancelPlanData.subscriptionId}`]}
                      style={{ minWidth: '80px' }}
                    >
                      {loadingStates[`cancel_${cancelPlanData.subscriptionId}`] ? 'Cancelling...' : 'Cancel Plan'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      {showUpgradeModal && upgradePlanData && (
        <UpgradePlanModal
          show={showUpgradeModal}
          onHide={() => {
            setShowUpgradeModal(false);
            setUpgradePlanData(null);
          }}
          currentPlan={upgradePlanData.currentPlan}
          targetPlan={upgradePlanData.targetPlan}
          preview={upgradePlanData.preview}
          onConfirm={handlePlanUpgrade}
          loading={loadingStates[`upgrade_${upgradePlanData.targetPlan.id}`]}
        />
      )}
    </Modal>
  );
};

export default AppsTrackingPlanModal;
